import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable
} from "@tanstack/react-table";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  CheckIcon,
  ChevronsUpDownIcon,
  EyeIcon,
  EyeOffIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import type {ColumnDef} from "@tanstack/react-table";
import type {ApiKey, ApiKeyEditFormData, LlmModel, PaginatedApiKeys} from "@/routes/_app/-secure-llm/-queries";
import {
  llmApiKeyDetailQueryOptions,
  llmApiKeyEditFormSchema,
  llmApiKeysQueryOptions,
  llmModelsQueryOptions,
  llmProvidersQueryOptions
} from "@/routes/_app/-secure-llm/-queries";
import { groupsListQueryOptions } from "@/lib/queries/groups";
import { api } from "@/lib/http";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useResourceAccessPermission } from "@/lib/perms";
import { Content as RadixPopoverContent } from "@radix-ui/react-popover";

type ApiKeyListItem = PaginatedApiKeys["items"][number];

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
});

export const Route = createFileRoute("/_app/llm-api-keys/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(llmApiKeysQueryOptions(deps));
  },
  staticData: {
    permissionGuards: [
      {
        resource: "llm_api_keys",
      },
    ],
  },
});

const columns = [
  {
    accessorKey: "name",
    header: "Name",
    size: 180,
  },
  {
    accessorKey: "api_key",
    header: "API Key",
    size: 220,
    cell: ({ row }) => <MaskedApiKey value={row.original.api_key} />,
  },
  {
    accessorKey: "description",
    header: "Description",
    size: 100,

    cell: ({ row }) => (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="max-w-[200px] truncate text-muted-foreground">
              {row.original.description}
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p>{row.original.description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    id: "llm_models",
    header: "LLM Models",
    size: 220,
    cell: ({ row }) => (
      <ModelBadges models={row.original.llm_models} limit={2} />
    ),
  },
  {
    id: "groups",
    header: "Groups",
    size: 220,
    cell: ({ row }) => <NameBadges items={row.original.groups} limit={2} />,
  },
  {
    id: "actions",
    header: "Actions",
    size: 60,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canEdit = useResourceAccessPermission("llm_api_keys", ["update"]);
      const canDelete = useResourceAccessPermission("llm_api_keys", ["delete"]);
      const [editOpen, setEditOpen] = React.useState(false);
      const [deleteOpen, setDeleteOpen] = React.useState(false);

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <MoreHorizontalIcon className="size-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit.data && (
                <DropdownMenuItem onSelect={() => setEditOpen(true)} className="gap-1">
                  <PencilIcon className="size-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete.data && (
                <DropdownMenuItem
                  onSelect={() => setDeleteOpen(true)}
                  className="gap-1"
                >
                  <TrashIcon className="size-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {canEdit.data && (
            <EditApiKeyDialog id={original.id} open={editOpen} onOpenChange={setEditOpen} />
          )}
          {canDelete.data && (
            <DeleteApiKeyDialog item={original} open={deleteOpen} onOpenChange={setDeleteOpen} />
          )}
        </>
      );
    },
  },
] satisfies Array<ColumnDef<ApiKeyListItem>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const apiKeys = useQuery(llmApiKeysQueryOptions(searchParams));

  const canEdit = useResourceAccessPermission("llm_api_keys", ["update"]);
  const canDelete = useResourceAccessPermission("llm_api_keys", ["delete"]);
  const canCreate = useResourceAccessPermission("llm_api_keys", ["create"]);

  const showActions = Boolean(canEdit.data || canDelete.data);

  const table = useReactTable({
    data: apiKeys.data?.items ?? [],
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page ?? 10,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: apiKeys.data?.count ?? 0,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">LLM API Keys</h1>
        {canCreate.data && (
          <CreateApiKeyDialog asChild>
            <Button>
              <PlusIcon />
              Create API Key
            </Button>
          </CreateApiKeyDialog>
        )}
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={apiKeys.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

export function CreateApiKeyDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: ApiKeyEditFormData) => {
      return api.raw.post("api/v1/llm/api-keys", { json: data }).json<ApiKey>();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["secureLLM", "admin", "api-keys"],
      });
      setOpen(false);
      toast.success(`API key "${data.name}" created successfully`);
    },
    onError: () => {
      toast.error("Failed to create API key");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            Add a new LLM API key for your organization.
          </DialogDescription>
        </DialogHeader>
        <ApiKeyForm
          key={open ? "open" : "closed"}
          mode="create"
          disabled={mutation.isPending}
          buttonText={mutation.isPending ? "Creating..." : "Create"}
          onSubmit={mutation.mutate}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

export function EditApiKeyDialog({
  children,
  asChild,
  id,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: React.PropsWithChildren<{
  asChild?: boolean;
  id: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  const queryClient = useQueryClient();

  const detail = useQuery({
    ...llmApiKeyDetailQueryOptions(id),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: async (data: ApiKeyEditFormData) => {
      const payload: Record<string, unknown> = { ...data };
      if (!payload.api_key) delete payload.api_key;
      return api.raw
        .put(`api/v1/llm/api-keys/${id}`, { json: payload })
        .json<ApiKey>();
    },
    onSuccess: (data) => {
      setOpen(false);
      queryClient.invalidateQueries({
        queryKey: ["secureLLM", "admin", "api-keys"],
      });
      queryClient.setQueryData(llmApiKeyDetailQueryOptions(id).queryKey, data);
      toast.success(`API key "${data.name}" updated successfully`);
    },
    onError: () => {
      toast.error("Failed to update API key");
    },
  });

  const initialProvider = detail.data?.llm_models?.[0]?.provider ?? "";
  const formDefaults = detail.data
    ? {
        name: detail.data.name,
        description: detail.data.description,
        api_key: "",
        llm_model_ids: detail.data.llm_models?.map((m) => m.id) ?? [],
        group_ids: detail.data.groups?.map((g) => g.id) ?? [],
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild={asChild}>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit API Key</DialogTitle>
          <DialogDescription>Update API key details.</DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : detail.data ? (
          <ApiKeyForm
            key={id}
            mode="edit"
            initialProvider={initialProvider}
            defaultValues={formDefaults}
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Saving..." : "Save Changes"}
            onSubmit={mutation.mutate}
            onCancel={() => setOpen(false)}
          />
        ) : (
          <div className="py-8 text-center text-sm text-destructive">
            Failed to load API key details.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DeleteApiKeyDialog({
  item,
  children,
  asChild,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: React.PropsWithChildren<{
  item: { id: string; name: string };
  asChild?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}>) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => {
      await api.raw.delete(`api/v1/llm/api-keys/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["secureLLM", "admin", "api-keys"],
      });
      setOpen(false);
      toast.success(`API key "${item.name}" deleted successfully`);
    },
    onError: () => {
      toast.error("Failed to delete API key");
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      {children && (
        <AlertDialogTrigger asChild={asChild ?? true}>
          {children}
        </AlertDialogTrigger>
      )}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete API Key</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{" "}
            <span className="font-semibold text-foreground">{item.name}</span>?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(item.id)}
          >
            {mutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ApiKeyForm({
  mode,
  initialProvider = "",
  defaultValues,
  disabled,
  buttonText,
  onSubmit,
  onCancel,
}: {
  mode: "create" | "edit";
  initialProvider?: string;
  defaultValues?: Partial<ApiKeyEditFormData>;
  disabled?: boolean;
  buttonText: React.ReactNode;
  onSubmit: (data: ApiKeyEditFormData) => void;
  onCancel: () => void;
}) {
  const form = useForm<ApiKeyEditFormData>({
    resolver: zodResolver(llmApiKeyEditFormSchema),
    defaultValues: {
      name: "",
      description: "",
      api_key: "",
      llm_model_ids: [],
      group_ids: [],
      ...defaultValues,
    },
  });

  const [provider, setProvider] = React.useState(initialProvider);
  const [providerOpen, setProviderOpen] = React.useState(false);
  const [providerSearch, setProviderSearch] = React.useState("");
  const [showApiKey, setShowApiKey] = React.useState(false);

  const providers = useQuery({
    ...llmProvidersQueryOptions(
      providerSearch ? { search: providerSearch } : undefined,
    ),
    placeholderData: keepPreviousData,
  });
  const models = useQuery({
    ...llmModelsQueryOptions({ provider: provider || undefined }),
    enabled: !!provider,
  });
  const groups = useQuery(groupsListQueryOptions({ page: 1, per_page: 100 }));

  const handleProviderChange = (v: string) => {
    setProvider(v);
    form.setValue("llm_model_ids", []);
  };

  const handleSubmit = form.handleSubmit((data) => {
    if (mode === "create" && !data.api_key) {
      form.setError("api_key", { message: "API key is required" });
      return;
    }
    onSubmit(data);
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="max-h-[65vh] space-y-4 overflow-y-auto px-1 pb-1"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter API key name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="api_key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                API Key{" "}
                {mode === "edit" && (
                  <span className="text-xs font-normal text-muted-foreground">
                    (leave blank to keep current)
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    placeholder={
                      mode === "edit" ? "Leave blank to keep current" : "sk-..."
                    }
                    className="pr-10"
                    {...field}
                    value={field.value ?? ""}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-0 right-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowApiKey((v) => !v)}
                  >
                    {showApiKey ? (
                      <EyeOffIcon className="size-4 text-muted-foreground" />
                    ) : (
                      <EyeIcon className="size-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter description"
                  className="max-h-24"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <Label>LLM Provider</Label>
          <Popover open={providerOpen} onOpenChange={setProviderOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={providerOpen}
                className="w-full justify-between font-normal"
              >
                <span className={cn(!provider && "text-muted-foreground")}>
                  {provider || "Select a provider"}
                </span>
                <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <RadixPopoverContent
              align="start"
              sideOffset={4}
              className={cn(
                "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
                "w-[--radix-popover-trigger-width] p-0",
              )}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search provider..."
                  value={providerSearch}
                  onValueChange={setProviderSearch}
                />
                <CommandList>
                  {providers.isLoading ? (
                    <CommandEmpty>Loading...</CommandEmpty>
                  ) : (providers.data ?? []).length === 0 ? (
                    <CommandEmpty>No providers found.</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {(providers.data ?? []).map((p) => (
                        <CommandItem
                          key={p}
                          value={p}
                          onSelect={() => {
                            handleProviderChange(p);
                            setProviderOpen(false);
                            setProviderSearch("");
                          }}
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 size-4",
                              provider === p ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {p}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </RadixPopoverContent>
          </Popover>
        </div>

        <FormField
          control={form.control}
          name="llm_model_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LLM Models</FormLabel>
              <FormControl>
                <div className="flex max-h-48 flex-wrap content-start gap-2 overflow-y-auto rounded-lg border p-3">
                  {!provider ? (
                    <p className="text-sm text-muted-foreground">
                      Select a provider first
                    </p>
                  ) : models.isLoading ? (
                    <p className="text-sm text-muted-foreground">
                      Loading models...
                    </p>
                  ) : (models.data ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No models available
                    </p>
                  ) : (
                    (models.data ?? []).map((m) => {
                      const selected = (field.value ?? []).includes(m.id);
                      return (
                        <Badge
                          key={m.id}
                          variant="outline"
                          className={
                            selected
                              ? "cursor-pointer border-primary bg-primary/10 text-primary ring-2 ring-primary/30"
                              : "cursor-pointer border-dashed text-muted-foreground hover:border-solid hover:text-foreground"
                          }
                          onClick={() => {
                            const current = field.value ?? [];
                            field.onChange(
                              selected
                                ? current.filter((id) => id !== m.id)
                                : [...current, m.id],
                            );
                          }}
                        >
                          {m.name}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="group_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Groups</FormLabel>
              <FormControl>
                <div className="flex max-h-48 flex-wrap content-start gap-2 overflow-y-auto rounded-lg border p-3">
                  {groups.isLoading ? (
                    <p className="text-sm text-muted-foreground">
                      Loading groups...
                    </p>
                  ) : (groups.data?.items ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No groups available
                    </p>
                  ) : (
                    (groups.data?.items ?? []).map((g) => {
                      const selected = (field.value ?? []).includes(g.id);
                      return (
                        <Badge
                          key={g.id}
                          variant="outline"
                          className={
                            selected
                              ? "cursor-pointer border-primary bg-primary/10 text-primary ring-2 ring-primary/30"
                              : "cursor-pointer border-dashed text-muted-foreground hover:border-solid hover:text-foreground"
                          }
                          onClick={() => {
                            const current = field.value ?? [];
                            field.onChange(
                              selected
                                ? current.filter((id) => id !== g.id)
                                : [...current, g.id],
                            );
                          }}
                        >
                          {g.name}
                        </Badge>
                      );
                    })
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export function MaskedApiKey({ value }: { value: string }) {
  const masked =
    value.length > 14 ? value.slice(0, 10) + "..." + value.slice(-4) : "****";
  return (
    <span className="font-mono text-sm text-muted-foreground select-none">
      {masked}
    </span>
  );
}

export function ModelBadges({
  models,
  limit,
}: {
  models: Array<LlmModel>;
  limit: number;
}) {
  const visible = models.slice(0, limit);
  const hidden = models.slice(limit);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((m) => (
        <Badge key={m.id} variant="outline" className="text-xs">
          {m.name}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="cursor-default border-transparent bg-muted text-xs text-muted-foreground"
              >
                +{hidden.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-xs bg-primary text-primary-foreground"
            >
              <p>{hidden.map((m) => m.name).join(", ")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export function NameBadges({
  items,
  limit,
}: {
  items: Array<{ id: string; name: string }>;
  limit: number;
}) {
  const visible = items.slice(0, limit);
  const hidden = items.slice(limit);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((item) => (
        <Badge key={item.id} variant="outline" className="text-xs">
          {item.name}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="cursor-default border-transparent bg-muted text-xs text-muted-foreground"
              >
                +{hidden.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-xs bg-primary text-primary-foreground"
            >
              <p>{hidden.map((item) => item.name).join(", ")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
